/* eslint-disable*/
import React, { useEffect, useState, useRef, useCallback } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faHome,
  faCalendarAlt,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import moment from "moment";
import { Chart, registerables } from "chart.js";
import { addMonths, subMonths, startOfMonth } from "date-fns";

// Register Chart.js components
Chart.register(...registerables);

const SubscriptionAnalysisChart = () => {
  const [loading, setLoading] = useState(false);
  const [chartInstances, setChartInstances] = useState({});
  const chartRefs = useRef({});

  // Granular loading states for different sections
  const [loadingStates, setLoadingStates] = useState({
    summary: false,
    status: false,
    stype: false,
    servicetype: false,
    service: false,
  });

  // --- Date Logic: Calculate Current Week (Mon-Sun) ---
  const defaultStart = moment().startOf("isoWeek");
  const defaultEnd = moment().endOf("isoWeek");
  const defaultLabel = `${defaultStart.format("DD-MM-YYYY")} - ${defaultEnd.format("DD-MM-YYYY")}`;

  // Form state - Initialized with Current Week
  const [formInputs, setFormInputs] = useState({
    created_on: defaultLabel,
    screated_on: defaultStart.toDate(),
    ecreated_on: defaultEnd.toDate(),
  });

  // Filter state to trigger API calls - Initialized with Current Week
  const [filters, setFilters] = useState({
    created_on: defaultLabel,
    screated_on: defaultStart.format("DD-MM-YYYY"),
    ecreated_on: defaultEnd.format("DD-MM-YYYY"),
  });

  // Data state matching API structure
  const [reportData, setReportData] = useState({
    totalSubscription: 0,
    totalActive: 0,
    totalPaid: 0,
    totalFree: 0,
    totalExpired: 0,
    totalRenewals: 0,
    allDates: [],
    chartData: {},
  });

  // Define Ranges for the Bootstrap Daterangepicker
  const ranges = {
    "This Week": [moment().startOf("isoWeek"), moment().endOf("isoWeek")],
    Today: [moment(), moment()],
    Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
    "Last 7 Days": [moment().subtract(6, "days"), moment()],
    "Last 30 Days": [moment().subtract(29, "days"), moment()],
    "This Month": [moment().startOf("month"), moment().endOf("month")],
    "Last Month": [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
    "This Year": [moment().startOf("year"), moment().endOf("year")],
    "Last Year": [
      moment().subtract(1, "year").startOf("year"),
      moment().subtract(1, "year").endOf("year"),
    ],
  };

  // Chart sections to render
  const chartSections = ["status", "stype", "servicetype", "service"];

  // Function to update loading state for a specific key
  const updateLoadingState = (key, value) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  // Fetch Data with granular loading
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Set all loading states to true initially
      const allKeys = Object.keys(loadingStates);
      const initialLoadState = allKeys.reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setLoadingStates(initialLoadState);

      const token = localStorage.getItem("authToken");

      const params = {};
      if (filters.created_on) {
        params.created_on = filters.created_on;
      }

      // Fetch main data
      const response = await axios.get(
        `${API_URL}report/subscription-analysis/chart`,
        {
          params: params,
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        }
      );

      if (response.data) {
        setReportData({
          totalSubscription: response.data.totalSubscription || 0,
          totalActive: response.data.totalActive || 0,
          totalPaid: response.data.totalPaid || 0,
          totalFree: response.data.totalFree || 0,
          totalExpired: response.data.totalExpired || 0,
          totalRenewals: response.data.totalRenewals || 0,
          allDates: response.data.allDates || [],
          chartData: response.data.chartData || {},
        });

        // Update summary loading state
        updateLoadingState('summary', false);

        // Update individual chart section loading states
        chartSections.forEach(key => {
          if (response.data.chartData[key]) {
            updateLoadingState(key, false);
          }
        });
      }
    } catch (error) {
      console.error("Error fetching subscription analysis:", error);

      // Set all loading states to false on error
      const allKeys = Object.keys(loadingStates);
      const errorLoadState = allKeys.reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
      setLoadingStates(errorLoadState);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Chart Lifecycle Management
  useEffect(() => {
    if (Object.keys(reportData.chartData).length > 0) {
      initializeCharts();
    }

    return () => {
      Object.values(chartInstances).forEach((chart) => {
        if (chart) chart.destroy();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData]);

  const initializeCharts = () => {
    const newChartInstances = {};

    chartSections.forEach((key) => {
      // Check if data exists for this key
      if (!reportData.chartData[key]) return;

      const currentData = reportData.chartData[key];
      const lineId = `lineChart_${key}`;
      const pieId = `pieChart_${key}`;
      const chartTitle = getChartTitle(key);

      // --- 1. Bar Chart (labeled as lineChart in IDs) ---
      if (chartRefs.current[lineId]) {
        // Destroy existing if any
        if (chartInstances[lineId]) chartInstances[lineId].destroy();

        const ctx = chartRefs.current[lineId].getContext("2d");

        newChartInstances[lineId] = new Chart(ctx, {
          type: "bar",
          data: {
            labels: reportData.allDates,
            datasets: Object.keys(currentData).map((label) => ({
              label: label,
              backgroundColor: currentData[label].color || "#ccc",
              data:
                typeof currentData[label].data === "string"
                  ? currentData[label].data.split(",").map(Number)
                  : [],
            })),
          },
          options: {
            plugins: {
              title: { display: true, text: chartTitle },
              legend: { position: "bottom" },
              tooltip: { mode: "index", intersect: false },
            },
            scales: {
              x: { stacked: true, grid: { display: false } },
              y: { stacked: true, beginAtZero: true },
            },
            responsive: true,
            maintainAspectRatio: false,
          },
        });
      }

      // --- 2. Pie Chart ---
      if (chartRefs.current[pieId]) {
        if (chartInstances[pieId]) chartInstances[pieId].destroy();

        const ctx = chartRefs.current[pieId].getContext("2d");

        newChartInstances[pieId] = new Chart(ctx, {
          type: "pie",
          data: {
            labels: Object.keys(currentData),
            datasets: [
              {
                data: Object.values(currentData).map((item) => {
                  // Sum piedata array if multiple values exist, or use totalcount
                  return Array.isArray(item.piedata)
                    ? item.piedata.reduce((a, b) => a + b, 0)
                    : Number(item.totalcount) || 0;
                }),
                backgroundColor: Object.values(currentData).map(
                  (item) => item.color || "#ccc"
                ),
              },
            ],
          },
          options: {
            plugins: {
              title: { display: true, text: chartTitle },
              legend: { position: "bottom" },
            },
            responsive: true,
            maintainAspectRatio: false,
          },
        });
      }
    });

    setChartInstances(newChartInstances);
  };

  // Helper for Titles
  const getChartTitle = (key) => {
    const titles = {
      status: "Service Status",
      stype: "Subscription Type",
      servicetype: "Service Type",
      service: "Services",
    };
    return titles[key] || key;
  };

  // Helper to calculate total count for the text under Pie Chart
  const getSectionTotal = (sectionData) => {
    if (!sectionData) return 0;
    return Object.values(sectionData).reduce(
      (acc, item) => acc + (Number(item.totalcount) || 0),
      0
    );
  };

  // --- Date Range Picker Handler ---
  const handleDateCallback = (event, picker) => {
    const start = picker.startDate;
    const end = picker.endDate;

    const dateValue = `${start.format("DD-MM-YYYY")} - ${end.format("DD-MM-YYYY")}`;

    setFormInputs((prev) => ({
      ...prev,
      created_on: dateValue,
      screated_on: start.toDate(),
      ecreated_on: end.toDate(),
    }));
  };

  const applyFilters = (e) => {
    e.preventDefault();
    setFilters({
      created_on: formInputs.created_on,
      screated_on: formInputs.screated_on
        ? moment(formInputs.screated_on).format("DD-MM-YYYY")
        : "",
      ecreated_on: formInputs.ecreated_on
        ? moment(formInputs.ecreated_on).format("DD-MM-YYYY")
        : "",
    });
  };

  // Skeleton loader for summary cards
  const SummarySkeleton = () => (
    <div className="row mb-4">
      {Array.from({ length: 6 }).map((_, index) => (
        <div className="col-sm-2" key={index}>
          <div className="widget widget-stats bg-gradient-skeleton skeleton-card">
            <div className="widget-stats-left">
              <div className="skeleton-text" style={{ width: "80%", height: "14px", marginBottom: "8px" }}></div>
            </div>
            <div className="widget-stats-right">
              <div className="skeleton-text" style={{ width: "50%", height: "24px" }}></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Skeleton loader for chart sections
  const ChartSectionSkeleton = () => (
    <React.Fragment>
      {/* Pie Chart Skeleton */}
      <div className="col-sm-6 mb-3">
        <div className="pie_chart_wrap">
          <div
            className="pie_chart_div"
            style={{ height: "380px", position: "relative" }}
          >
            <div className="skeleton-chart" style={{ height: "100%", width: "100%" }}>
              <div className="skeleton-pie-chart"></div>
            </div>
          </div>
          <div className="total_value_div text-center">
            <div className="skeleton-text" style={{ width: "120px", height: "18px", margin: "10px auto" }}></div>
          </div>
        </div>
      </div>

      {/* Bar Chart Skeleton */}
      <div className="col-sm-6">
        <div style={{ height: "365px", position: "relative" }}>
          <div className="skeleton-chart" style={{ height: "100%", width: "100%" }}>
            <div className="skeleton-bar-chart"></div>
          </div>
        </div>
      </div>

      {/* Divider for visual separation between sections */}
      <hr className="col-12 my-4" />
    </React.Fragment>
  );

  return (
    <div className="page-header-fixed page-sidebar-fixed fade in mt-5">
      {/* Injecting the requested CSS Link */}
      <link href="https://research.streetgains.in/dev/app/assets/daterangepicker.css" rel="stylesheet" />

      {loading}

      {/* Breadcrumb */}
      <div className="row pb-2">
        <div className="col-md-3">
          <ul className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard" style={{ textDecoration: "none" }}>
                <FontAwesomeIcon icon={faHome} /> Home
              </a>
            </li>
            <li className="breadcrumb-item active">
              Subscription Analysis Chart
            </li>
          </ul>
        </div>
      </div>

      <div className="panel panel-default with-rounded-corner with-shadow chartbox">
        <div className="panel-heading"></div>
        <div className="panel-body">
          {/* Filter Form */}
          <div className="row">
            <div className="col-12">
              <form onSubmit={applyFilters}>
                <div className="row align-items-center">
                  <div className="col-md-2">
                    <div className="form-group">
                      <label className="control-label">Created Date</label>

                      {/* Using React Bootstrap DateRangePicker */}
                      <DateRangePicker
                        initialSettings={{
                          autoUpdateInput: false,
                          locale: {
                            format: "DD-MM-YYYY",
                            cancelLabel: "Clear",
                          },
                          ranges: ranges,
                          // Use State values for Start/End to ensure picker matches input
                          startDate: formInputs.screated_on
                            ? moment(formInputs.screated_on)
                            : moment(),
                          endDate: formInputs.ecreated_on
                            ? moment(formInputs.ecreated_on)
                            : moment(),
                        }}
                        onApply={handleDateCallback}
                      >
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            value={formInputs.created_on}
                            placeholder="Select Date Range"
                            readOnly
                          />
                          <span
                            className="input-group-addon"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "0 10px",
                              background: "#eee",
                              border: "1px solid #ced4da",
                              borderLeft: "none",
                            }}
                          >
                            <FontAwesomeIcon icon={faCalendarAlt} />
                          </span>
                        </div>
                      </DateRangePicker>

                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <label className="control-label w-100"></label>
                      <button type="submit" className="btn btn-default">
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Summary Widgets with Skeleton Loading */}
          {loadingStates.summary ? (
            <SummarySkeleton />
          ) : (
            <div className="row mb-4">
              {/* Total Subscription */}
              <div className="col-sm-2">
                <div className="widget widget-stats bg-gradient-blue-dark inverse-mode">
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">Total Subscription</div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      {reportData.totalSubscription}
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Active */}
              <div className="col-sm-2">
                <div className="widget widget-stats bg-gradient-orange inverse-mode">
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">Total Active</div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      {reportData.totalActive}
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Paid */}
              <div className="col-sm-2">
                <div className="widget widget-stats bg-gradient-red inverse-mode">
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">Total Paid</div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      {reportData.totalPaid}
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Free */}
              <div className="col-sm-2">
                <div className="widget widget-stats bg-gradient-purple inverse-mode">
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">Total Free</div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      {reportData.totalFree}
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Expired */}
              <div className="col-sm-2">
                <div className="widget widget-stats bg-gradient-blue-purple inverse-mode">
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">Total Expired</div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      {reportData.totalExpired}
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Renewals */}
              <div className="col-sm-2">
                <div className="widget widget-stats bg-gradient-red-orange inverse-mode">
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">Total Renewals</div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      {reportData.totalRenewals}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Dynamic Charts Area with Skeleton Loading */}
          <div className="row">
            {chartSections.map((key) => {
              // Show skeleton if loading, otherwise check if data exists
              if (loadingStates[key]) {
                return (
                  <React.Fragment key={key}>
                    <div className="col-12 text-center my-2">
                      <h4 className="skeleton-text" style={{ width: "180px", margin: "0 auto", height: "24px" }}></h4>
                    </div>
                    <ChartSectionSkeleton />
                  </React.Fragment>
                );
              }

              // Only render actual charts if data exists
              if (!reportData.chartData[key]) return null;

              return (
                <React.Fragment key={key}>
                  <div className="col-12 text-center my-2">
                    <h4>{getChartTitle(key)}</h4>
                  </div>

                  {/* Pie Chart Section */}
                  <div className="col-sm-6 mb-3">
                    <div className="pie_chart_wrap">
                      <div
                        className="pie_chart_div"
                        style={{ height: "380px", position: "relative" }}
                      >
                        <canvas
                          ref={(el) =>
                            (chartRefs.current[`pieChart_${key}`] = el)
                          }
                          id={`pieChart_${key}`}
                        ></canvas>
                      </div>
                      <div className="total_value_div text-center">
                        <label>
                          Total Count:{" "}
                          <span>
                            {getSectionTotal(reportData.chartData[key])}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Bar/Line Chart Section */}
                  <div className="col-sm-6">
                    <div style={{ height: "365px", position: "relative" }}>
                      <canvas
                        ref={(el) =>
                          (chartRefs.current[`lineChart_${key}`] = el)
                        }
                        id={`lineChart_${key}`}
                      ></canvas>
                    </div>
                  </div>

                  {/* Divider for visual separation between sections */}
                  <hr className="col-12 my-4" />
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <style jsx>{`
        /* Existing Structure Styles */
        .breadcrumb {
          background: transparent;
          margin-bottom: 0;
          padding: 0;
          list-style: none;
          display: flex;
          align-items: center;
        }
        .breadcrumb-item + .breadcrumb-item::before {
          content: ">";
          padding: 0 8px;
          color: #6c757d;
        }
        .breadcrumb-item a {
          color: #007bff;
          text-decoration: none;
        }
        .breadcrumb-item.active {
          color: #6c757d;
        }

        /* Widget Styles */
        .widget-stats {
          position: relative;
          padding: 15px;
          border-radius: 4px;
          min-height: 80px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: #fff;
          text-decoration: none;
          overflow: hidden;
          margin-bottom: 10px;
        }
        .widget-stats:hover {
          color: #fff;
          opacity: 0.9;
        }
        .widget-stats:before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.1);
          z-index: 0;
        }
        .widget-stats-left,
        .widget-stats-right {
          position: relative;
          z-index: 10;
        }
        .widget-stats-title {
          font-size: 12px;
          font-weight: 600;
          opacity: 0.9;
          margin-bottom: 5px;
          text-transform: uppercase;
        }
        .widget-stats-value {
          font-size: 24px;
          font-weight: 700;
        }

        /* Gradients */
        .bg-gradient-blue-dark {
          background: linear-gradient(45deg, #2c3e50, #3498db);
        }
        .bg-gradient-orange {
          background: linear-gradient(45deg, #e67e22, #e74c3c);
        }
        .bg-gradient-red {
          background: linear-gradient(45deg, #c0392b, #e74c3c);
        }
        .bg-gradient-purple {
          background: linear-gradient(45deg, #6b5b95, #b565a7);
        }
        .bg-gradient-blue-purple {
          background: linear-gradient(45deg, #3f51b5, #9c27b0);
        }
        .bg-gradient-red-orange {
          background: linear-gradient(45deg, #ff5252, #ff9800);
        }
        .bg-gradient-green {
          background: linear-gradient(45deg, #27ae60, #2ecc71);
        }

        /* Skeleton Loading Styles */
        .skeleton-text {
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          );
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
          border-radius: 4px;
          display: inline-block;
        }

        .skeleton-card {
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          ) !important;
          background-size: 200% 100% !important;
          animation: skeleton-loading 1.5s ease-in-out infinite !important;
          min-height: 80px;
        }

        .skeleton-chart {
          position: relative;
          overflow: hidden;
          border-radius: 4px;
          background: #f8f9fa;
        }

        .skeleton-pie-chart,
        .skeleton-bar-chart {
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          );
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
        }

        .skeleton-pie-chart {
          border-radius: 50%;
          width: 70% !important;
          height: 70% !important;
          margin: 15% auto;
        }

        .skeleton-bar-chart {
          display: flex;
          align-items: flex-end;
          justify-content: space-around;
          padding: 30px 20px;
        }

        .skeleton-bar-chart::before,
        .skeleton-bar-chart::after {
          content: '';
          width: 20%;
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          );
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
          border-radius: 4px;
        }

        .skeleton-bar-chart::before {
          height: 60%;
        }

        .skeleton-bar-chart::after {
          height: 80%;
        }

        @keyframes skeleton-loading {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }

        /* Chart Containers */
        .pie_chart_wrap {
          position: relative;
        }
        .total_value_div {
          margin-top: 10px;
          font-weight: bold;
        }
        .total_value_div span {
          font-weight: 600;
        }

        /* General UI */
        .panel {
          background: white;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .form-group {
          margin-bottom: 1rem;
        }
        .control-label {
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: block;
        }
        .btn-default {
          background-color: #f8f9fa;
          border-color: #ddd;
          color: #333;
        }
        .btn-default:hover {
          background-color: #e9ecef;
        }

        /* Ensure the input group aligns correctly with the button */
        .input-group {
          display: flex;
          width: 100%;
        }
        .input-group .form-control {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          cursor: pointer;
          background: #fff !important;
        }
        .input-group-addon {
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }
      `}</style>
    </div>
  );
};

export default SubscriptionAnalysisChart;